import {
  AUTH_JWT_TOKEN,
  AUTH_JWT_TOKEN_EXPIRATION,
  AUTH_REFRESH_TOKEN,
  AUTH_REFRESH_TOKEN_EXPIRATION,
  Box,
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  Button,
  Card,
  PageRoute,
  Stack,
  Typography,
  useParams,
  useTheme,
  useValidationErrors,
} from "@compass/core";
import { AuthResultType, AuthViewResource, SimpleDto } from "generated/openApi";
import { AuthPageWrapper } from "pages/auth/components/AuthPageWrapper";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ApiService from "services/apiService";
import { setAppAuthStatus } from "redux/reducers/appSlice";
import { Input } from "components";

const routeName = "tenantList";

class OptionalParams {
  redirectTo?: string = "";
}

export const tenantListPageRoute: PageRoute<undefined, OptionalParams> = {
  handle: "tenantList",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
  getOptionalParams: (params: OptionalParams) => buildOptionalParams(params),
};

export const TenantListPage = () => {
  const { optionalParams } = useParams(routeName, undefined, OptionalParams);
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [searchTerm, setSearchTerm] = useState("");
  const [viewResource, setViewResource] = useState<AuthViewResource>({});
  const [userTenants, setUserTenants] = useState<SimpleDto[]>([]);
  const { validationErrors, setValidationErrors } = useValidationErrors();

  const properties = viewResource.properties || [];

  useEffect(() => {
    ApiService.users.getCentralUserTenants().then((response) => {
      setUserTenants(response);
    });
  }, []);

  const filteredTenants = useMemo(() => {
    return userTenants.filter((tenant) =>
      tenant.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [userTenants, searchTerm]);

  const handleUpdateTenant = (tenant: SimpleDto) => {
    ApiService.authV2.swapTenant(tenant.id).then((response) => {
      if (response.authResultType === AuthResultType.Authenticated) {
        localStorage.setItem(AUTH_JWT_TOKEN, response.jwtToken!);
        localStorage.setItem(AUTH_JWT_TOKEN_EXPIRATION, response.jwtTokenExpiration?.toString()!);
        localStorage.setItem(AUTH_REFRESH_TOKEN, response.refreshToken!);
        localStorage.setItem(AUTH_REFRESH_TOKEN_EXPIRATION, response.refreshTokenExpiration?.toString()!);

        dispatch(setAppAuthStatus("AUTHENTICATED"));
        navigate("/");
        window.location.reload();
      }
    })
  };

  const renderTenant = (tenant: SimpleDto, onSelect: () => void) => {
    return (
      <Button
        onClick={onSelect}
        sx={{
          width: '100%',
          textAlign: 'left',
          p: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',

          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            borderColor: 'primary.main',

            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          },
        }}
      >
        <Stack direction="row" spacing="sm" alignItems="center" sx={{ width: '100%' }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }} >
              {tenant.name}
            </Typography>
          </Box>

          <ChevronRightIcon />
        </Stack>
      </Button>
    );
  };


  return (
    <AuthPageWrapper>
      <Card sx={{ maxWidth: "500px", minHeight:"600px", maxHeight: "600px", width: "100%", overflow: 'hidden' }}>
        <Stack px={3} pt={5} pb={3} spacing="md">
          <Stack spacing="sm" alignItems="center">
            <Typography variant="h6" fontWeight="bold">
              Welcome Back
            </Typography>
            <Typography variant="body2" color={theme.palette.grey[500]}>
              Select a Grant Program to continue:
            </Typography>

            <Box sx={{ flexGrow: 1 }}>
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
              />
            </Box>
          </Stack>

          <Box
            sx={{
              maxHeight: '350px',
              minHeight: '350px', 
              overflowY: 'auto',
              py: 1,
              pr: 1,
            }}
          >
            <Stack spacing="md" pb={2}>
              {filteredTenants?.map((tenant) => (
                renderTenant(
                  tenant,
                  () => handleUpdateTenant(tenant)
                )
              ))}
            </Stack>
          </Box>

        </Stack>
      </Card>
    </AuthPageWrapper>
  );
};